import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext } from "react";
import { createUseStyles } from "react-jss";
import { components } from "react-select";
import { fastDestAutoComplete, openTableAutocomplete } from "../../../../api";
import { cStyler } from "../../../../helpers";
import { StyleTypeContext } from "../../../../umbilical_tools";
import AsyncSelect from "react-select/async";
import { Field } from "formik";
import { autoCompleteInputStyles } from "..";
import jssVariables from "../../../../jss_setup";

const asyncSelectInputStyles = createUseStyles({
  mobileViewAsyncSelect: (props) => ({
    display: "block",
    ..._.get(props, "customStyles.mobileViewAsyncSelect", {}),
  }),
  desktopViewAsyncSelect: (props) => ({
    display: "none",
    ..._.get(props, "customStyles.desktopViewAsyncSelect", {}),
  }),
});

// ========================= SEARCH FUNCTIONS ========================
async function destSearch({ query }) {
  var data = await fastDestAutoComplete({
    name: _.trim(query.toLowerCase()),
  });

  const dests = _.get(data, "destinations", [])
    .filter((d) => d.score > 0.1)
    .sort((a, b) => b.score - a.score)
    .filter((i, idx) => idx < 20)
    .map((dest) => {
      const label = [...new Set(dest.destination.split("-"))].join(" - ");
      return {
        ...dest,
        label,
        value: label,
        location_type: "destination",
      };
    });

  return dests;
}

async function restaurantSearch({ query }) {
  var data = await openTableAutocomplete({
    name: _.trim(query.toLowerCase()),
  });

  const rests = _.get(data, "restaurants", []).map((rest) => {
    const label = `${rest.name}, ${rest.address}, ${rest.country}`;
    return {
      ...rest,
      label,
      value: label,
      location_type: "restaurant",
    };
  });
  return rests;
}

function promiseOptions(query, types) {
  return new Promise((resolve) => {
    const promises = [destSearch({ query }), restaurantSearch({ query })];

    resolve(
      Promise.all(promises).then(([dests, rests]) => [
        ...(dests || []),
        ...(rests || []),
      ])
    );
  });
}

const debouncedSearch = _.debounce((query, types, callback) => {
  if (query.length < 3) {
    callback([]);
    return;
  }

  promiseOptions(query, types).then((result) => callback(result));
}, 300);

// =============== OPTIONS AND INPUT CONTROL COMPONENTS ==============

const optionItemStyles = createUseStyles({
  displayContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    width: "70vw",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.displayContainer", {}),
  }),
  displayLabel: (props) => ({
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflowX: "hidden",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.displayLabel", {}),
  }),
  icon: (props) => ({
    width: "2rem",
    ..._.get(props, "customStyles.icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    displayContainer: (props) => ({
      width: "unset",
      ..._.get(props, "customStyles.displayContainer", {}),
    }),
  },
});
const OptionItem = ({ label, data, ...props }) => {
  const classes = optionItemStyles();
  return (
    <components.Option {...props}>
      <span className={classes.displayContainer}>
        <span className={classes.iconContainer}>
          <InlineIcon
            icon={
              Object.keys(data).includes("rid")
                ? "fluent:food-16-regular"
                : "fa:map-marker"
            }
            className={classes.icon}
          />
        </span>
        <span className={classes.displayLabel}>{label}</span>
      </span>
    </components.Option>
  );
};
const inputControlSideFormStyles = createUseStyles({
  displayContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    width: "70vw",
    alignItems: "center",
    color: jssVariables.colors.text.dark,
    paddingLeft: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.displayContainer", {}),
  }),
  iconContainer: (props) => ({
    padding: 0,
    ..._.get(props, "customStyles.iconContainer", {}),
  }),
  displayLabel: (props) => ({
    width: "100%",
    ..._.get(props, "customStyles.displayLabel", {}),
  }),
  icon: (props) => ({
    width: `calc(${jssVariables.normal_gap} * 2)`,
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    displayContainer: (props) => ({
      width: "17rem",
      ..._.get(props, "customStyles.displayContainer.smallscreen", {}),
    }),
  },
});
const InputControlSideForm = ({ children, ...props }) => {
  const { type, brand } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "destRestcompleteControlStyles",
  });

  const classes = inputControlSideFormStyles({ customStyles });

  return (
    <components.Control {...props}>
      <span className={classes.displayContainer}>
        <span className={classes.iconContainer}>
          <InlineIcon icon="fa:map-marker" className={classes.icon} />
        </span>
        <span className={classes.displayLabel}>{children}</span>
      </span>
    </components.Control>
  );
};

const inputControlMainFormStyles = createUseStyles({
  displayContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr",
    gridGap: `calc(${jssVariables.normal_gap} / 2)`,
    width: "70vw",
    alignItems: "center",
    color: jssVariables.colors.text.dark,
    paddingLeft: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.displayContainer", {}),
  }),
  iconContainer: (props) => ({
    padding: 0,
    ..._.get(props, "customStyles.iconContainer", {}),
  }),
  displayLabel: (props) => ({
    width: "100%",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.displayLabel", {}),
  }),
  icon: (props) => ({
    width: `calc(${jssVariables.normal_gap} * 2)`,
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.icon", {}),
  }),

  [`@media ${jssVariables.media.smallscreen}`]: {
    displayContainer: (props) => ({
      width: "20rem",
      ..._.get(props, "customStyles.displayContainer.smallscreen", {}),
    }),
  },
});
const InputControlMainForm = ({ children, ...props }) => {
  const { type, brand } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "destRestcompleteControlStyles",
  });
  const classes = inputControlMainFormStyles({ customStyles });

  return (
    <components.Control {...props}>
      <span className={classes.displayContainer}>
        <span className={classes.iconContainer}>
          <InlineIcon icon="fa:map-marker" className={classes.icon} />
        </span>
        <span className={classes.displayLabel}>{children}</span>
      </span>
    </components.Control>
  );
};

// ============= MAIN REACT SELECT AUTOCOMPLETE COMPONENT ============

const selectorStyles = () => {
  return {
    placeholder: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
      fontSize: "medium",
    }),
    singleValue: (base) => ({
      ...base,
      color: jssVariables.colors.text.dark,
    }),
    control: (base) => ({
      ...base,
      border: `1px solid ${jssVariables.colors.border}`,
      height: "3rem",
    }),
    input: (base) => ({
      ...base,
      height: `calc(${jssVariables.normal_gap} * 3.2)`,
      alignContent: "center",
      color: jssVariables.colors.text.dark,
    }),
    indicatorsContainer: () => ({
      display: "none",
    }),
    valueContainer: (base) => ({
      ...base,
      justifyItems: "start",
      paddingLeft: 0,
      color: jssVariables.colors.text.dark,
    }),
    option: (base) => ({
      ...base,
      width: "98%",
    }),
  };
};
const DestRestAutocompleteRSelect = ({
  types,
  placeholder,
  onSelect,
  customStyles,
  isSideForm,
  value,
}) => {
  const classes = asyncSelectInputStyles({ customStyles });

  var commonProps = {
    isClearable: true,
    defaultOptions: true,
    blurInputOnSelect: true,
    loadOptions: (query, callback) => debouncedSearch(query, types, callback),
    onChange: (item) => {
      if (!item) {
        return;
      }
      onSelect({
        restaurant_rid: item.rid ? item.rid : null,
        restaurant_meta: item.rid ? item : null,
        destination_code: item.rid ? null : [`${item.type}___${item.id}`],
        destination_meta: item.rid ? null : item,
      });
    },
    placeholder,
    components: {
      Option: OptionItem,
      Control: isSideForm ? InputControlSideForm : InputControlMainForm,
    },
    value,
  };

  return (
    <React.Fragment>
      <span className={classes.mobileViewAsyncSelect}>
        <AsyncSelect
          {...commonProps}
          styles={selectorStyles()}
          menuPlacement="top"
        />
      </span>
    </React.Fragment>
  );
};
DestRestAutocompleteRSelect.defaultProps = {
  types: ["dest"],
  placeholder: "",
};
DestRestAutocompleteRSelect.propTypes = {
  types: PropTypes.array.isRequired,
  placeholder: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const DestRestAutoCompleteInputComponent = ({
  field,
  form: { touched, errors, setFieldValue },
  types,
  value,
  styles,
  ...props
}) => {
  return (
    <React.Fragment>
      <DestRestAutocompleteRSelect
        types={types}
        onSelect={(dest) => {
          setFieldValue(field.name, dest);
        }}
        value={value}
        {...props}
      />
      {touched[field.name] && errors[field.name] && (
        <div className={styles.errorMessage}>{errors[field.name]}</div>
      )}
    </React.Fragment>
  );
};
export const DestRestAutoCompleteInputField = ({
  label,
  customStyles,
  ...props
}) => {
  const classes = autoCompleteInputStyles({ customStyles });

  return (
    <div className={classes.inputGroup}>
      <label className={classes.label}>{label}</label>
      <Field
        styles={classes}
        {...props}
        customStyles={customStyles}
        component={DestRestAutoCompleteInputComponent}
      />
    </div>
  );
};
DestRestAutoCompleteInputField.defaultProps = {
  name: "",
  types: [],
  placeholder: "",
  customStyles: {},
  isInFilters: false,
};
DestRestAutoCompleteInputField.propTypes = {
  name: PropTypes.string.isRequired,
  types: PropTypes.array,
  placeholder: PropTypes.string,
  customStyles: PropTypes.object,
  isInFilters: PropTypes.bool,
};
